import { IconDownload } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { useApplicationsTableState } from 'components/Dashboard/ApplicationsTable/ApplicationsTableState.context';
import { COLUMN_IDS } from 'components/Dashboard/ApplicationsTable/ApplicationsTableState.context.helpers';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { CSVLink } from 'react-csv';
import { Tooltip } from 'react-tooltip';
import { APPLICATION_STATUS_LABEL } from 'utils/applications/applications.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './ExportCSVButton.module.css';

export const ExportCSVButton = () => {
  const { i18n } = useLocale();
  const { table } = useApplicationsTableState();
  const numberFormat = useNumberFormat();
  const { trackEvent } = useAmplitude();

  if (!table || table.getRowCount() === 0) {
    return (
      <Button disabled size="small" variant="secondary-alt">
        <IconDownload />
        <Trans>Export as CSV</Trans>
      </Button>
    );
  }

  const formatDate = (value?: string) =>
    value
      ? i18n.date(value, { day: '2-digit', month: 'short', year: '2-digit' })
      : '-';

  const formatCurrency = (value?: number) =>
    value && value !== -1 ? numberFormat.formatCurrency(value) : '-';

  const processCellValue = (columnId: string, value: any): string => {
    switch (columnId) {
      case COLUMN_IDS.created:
        return formatDate(value);
      case COLUMN_IDS.applicant:
        return t`[Anonymised]`;
      case COLUMN_IDS.expiryDate:
        return formatDate(value);
      case COLUMN_IDS.approvalAmount:
      case COLUMN_IDS.requestedAmount:
        return formatCurrency(value);
      case COLUMN_IDS.status:
        return APPLICATION_STATUS_LABEL(value);
      case COLUMN_IDS.referredBy:
        return `${value.first_name} ${value.last_name}`;
      default:
        return value;
    }
  };

  const csvHeaders = table.getVisibleLeafColumns().map((col) => ({
    label: String(col.columnDef.header),
    key: col.id,
  }));

  const csvData = table.getRowModel().rows.map((row) => {
    const rowData: Record<string, string> = {};
    row.getVisibleCells().forEach((cell) => {
      rowData[cell.column.id] = processCellValue(
        cell.column.id,
        cell.getValue(),
      );
    });
    return rowData;
  });

  const fileName = `${t`export`}_${new Date().toLocaleDateString().replaceAll('/', '-')}_${new Date().toLocaleTimeString().replaceAll(':', '-')}.csv`;

  return (
    <CSVLink
      data-testid="csv-link"
      headers={csvHeaders}
      data={csvData}
      filename={fileName}
      target="_blank"
      onClick={() => trackEvent(AMPLITUDE_EVENTS.exportCsvButtonClicked)}
    >
      <Button variant="secondary-alt" size="small" data-tooltip-id="export-csv">
        <IconDownload />
        <Trans>Export as CSV</Trans>
        <Tooltip
          className={styles.tooltip}
          id="export-csv"
          opacity={1}
          content={t`Export the current list of applications.`}
          openEvents={{ focus: false, mouseenter: true }}
          place="bottom"
        />
      </Button>
    </CSVLink>
  );
};
